import { GiHamburgerMenu } from '@react-icons/all-files/gi/GiHamburgerMenu';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { ReactDimmer } from 'react-dimmer';
import { CSSProperties } from 'styled-components';
import { trackEvent } from '../../lib/track';
import { IPlugin } from '../../types';
import { StyledAside, StyledHeader, StyledMiniHeader } from './header.styles';
import {
	AppsPopover,
	ResourcesPopover,
	SupportPopover,
} from './menuPopovers/menuPopovers.comp';

// @ts-ignore
import apps from 'apps.json';

import imageWhite from '../../../public/assets/cn-logo-dark.svg';
import imageBlack from '../../../public/assets/cn-logo-light.svg';

interface IHeaderProps {
	style?: CSSProperties;
	isDark?: boolean;
	isDeveloper?: boolean;
	className?: string;
	logoPath?: '/' | '';
}

export const Header = ({
	style = {},
	isDark = false,
	isDeveloper = false,
	className = '',
	logoPath = '',
}: IHeaderProps) => {
	const [showAppsPopover, setShowAppsPopover] = useState<boolean>(false);
	const [showResourcesPopover, setShowResourcesPopover] =
		useState<boolean>(false);
	const [showSupportPopover, setShowSupportPopover] = useState<boolean>(false);
	const [sideNavOpen, setSideNavOpen] = useState<boolean>(false);
	const router = useRouter();

	const tagList = (apps as any).reduce((acc: string[], plugin: IPlugin) => {
		plugin.tags.forEach((tag) => {
			if (tag && !acc.includes(tag)) {
				acc.push(tag);
			}
		});
		return acc;
	}, []);

	const columns = tagList.splice(0, 3).map((tag: string) => {
		const wantedApps = apps
			.filter((p: any) => p.tags.includes(tag))
			.slice(0, 5);
		return { title: tag?.split('-')?.[0], apps: wantedApps };
	});

	// content={<AppsPopover columns={columns} isDark={isDark} show={showAppsPopover} setShow={setShowAppsPopover} />}
	function renderLinks() {
		return (
			<div className="links">
				<div
					className="link-wrapper"
					onMouseEnter={() => setShowAppsPopover(true)}
					onMouseLeave={() => setShowAppsPopover(false)}
				>
					<div>
						<Link href="https://www.commoninja.com/widgets?utm_source=discover&utm_medium=referral&utm_campaign=widgets">
							Widgets
						</Link>
						<AppsPopover
							show={showAppsPopover}
							columns={columns}
							isDark={isDark}
						/>
					</div>
				</div>
				<div>
					<Link href="https://www.commoninja.com/pricing">Pricing</Link>
				</div>
				<div
					className="link-wrapper"
					onMouseEnter={() => setShowResourcesPopover(true)}
					onMouseLeave={() => setShowResourcesPopover(false)}
				>
					<div>
						<Link href="https://www.commoninja.com/resources">Resources</Link>
						{/* <p className="link">Resources</p> */}
						<ResourcesPopover show={showResourcesPopover} isDark={isDark} />
					</div>
				</div>
				<div
					className="link-wrapper"
					onMouseEnter={() => setShowSupportPopover(true)}
					onMouseLeave={() => setShowSupportPopover(false)}
				>
					<div>
						<Link href="https://www.commoninja.com/support">Support</Link>
						{/* <p className="link">Support</p> */}
						<SupportPopover show={showSupportPopover} isDark={isDark} />
					</div>
				</div>
			</div>
		);
	}

	function renderSideNav() {
		return (
			<>
				<StyledMiniHeader
					style={style}
					className={`${isDark ? 'dark' : ''} ${className}`}
				>
					<nav>
						<Link href={'/'} className="logo">
							{isDark ? (
								<Image
									src={imageWhite}
									alt="Common ninja logo"
									width={200}
									height={30}
								/>
							) : (
								<Image
									src={imageBlack}
									alt="Common ninja logo"
									width={200}
									height={30}
								/>
							)}
						</Link>
						<GiHamburgerMenu
							onClick={() => setSideNavOpen(true)}
							fontSize={23}
							className={`hamburger ${isDark ? 'dark' : ''}`}
						/>
					</nav>
				</StyledMiniHeader>
				<StyledAside
					className={`${isDark ? 'dark' : ''} ${sideNavOpen ? 'opened' : ''}`}
				>
					<div className="top">
						<Link onClick={() => setSideNavOpen(false)} href={'/'}>
							{isDark ? (
								<Image
									src={imageWhite}
									alt="Common ninja logo"
									width={200}
									height={30}
								/>
							) : (
								<Image
									src={imageBlack}
									alt="Common ninja logo"
									width={200}
									height={30}
								/>
							)}
						</Link>
					</div>
					<div className="content">
						<nav>
							<Link
								className={`${router.pathname === '/widgets' ? 'active' : ''}`}
								onClick={() => setSideNavOpen(false)}
								href="https://www.commoninja.com/widgets"
							>
								Widgets
							</Link>
							<Link
								className={`${
									router.pathname === '/platforms' ? 'active' : ''
								}`}
								onClick={() => setSideNavOpen(false)}
								href="https://www.commoninja.com/platforms"
							>
								Platforms
							</Link>
							<Link
								className={`${router.pathname === '/pricing' ? 'active' : ''}`}
								onClick={() => setSideNavOpen(false)}
								href="https://www.commoninja.com/pricing"
							>
								Pricing
							</Link>
							<div className="sub-items">
								<p className="title">Resources</p>
								<div className="items">
									<Link
										className={`${router.pathname === '/blog' ? 'active' : ''}`}
										onClick={() => setSideNavOpen(false)}
										href="https://www.commoninja.com/blog"
									>
										Blog
									</Link>
									<Link
										href="https://developers.commoninja.com"
										target={'_blank'}
									>
										Developers
									</Link>
									<Link
										href="https://help.commoninja.com/hc/en-us/community/topics/360000419377-General-Discussion"
										target={'_blank'}
									>
										Community Forum
									</Link>
									<Link
										href="https://help.commoninja.com/hc/en-us/community/topics/360000419337-Feature-Requests"
										target={'_blank'}
									>
										Feature Request
									</Link>
									<Link
										className={`${
											router.pathname === '/affiliates' ? 'active' : ''
										}`}
										onClick={() => setSideNavOpen(false)}
										href="https://www.commoninja.com/affiliates"
									>
										Affiliate Program
									</Link>
									<Link
										className={`${
											router.pathname === '/write-for-us' ? 'active' : ''
										}`}
										onClick={() => setSideNavOpen(false)}
										href="https://www.commoninja.com/write-for-us"
									>
										Write for us
									</Link>
								</div>
							</div>
							<div className="sub-items">
								<p className="title">Support</p>
								<div className="items">
									<Link
										className={`${router.pathname === '/' ? '' : ''}`}
										href="https://help.commoninja.com/hc/en-us"
										target={'_blank'}
									>
										Help Center
									</Link>
									<Link
										className={`${router.pathname === '/' ? '' : ''}`}
										href="https://www.commoninja.com/contact-us"
										target={'_blank'}
									>
										Contact Us
									</Link>
								</div>
							</div>
							<div className="sub-items">
								<p className="title">More</p>
								<div className="items">
									<Link
										onClick={() => setSideNavOpen(false)}
										href="https://docs.google.com/forms/u/2/d/e/1FAIpQLScSaQ2nwvM9gkKLmmUnrw_IIqB5s-f3CxLGI2ieX8K2EFaWrw/viewform?usp=send_form"
										target={'_blank'}
									>
										Submit an App
									</Link>
								</div>
							</div>
						</nav>
					</div>
					<div className="bottom">
						<Link
							href="https://www.commoninja.com/login"
							onClick={() => {
								trackEvent('Website Login');
							}}
						>
							Log In
						</Link>
					</div>
				</StyledAside>
			</>
		);
	}

	return (
		<>
			<ReactDimmer isOpen={sideNavOpen} exitDimmer={setSideNavOpen} blur={1} />
			{renderSideNav()}
			<StyledHeader style={style} className={`${isDark ? 'dark' : ''}`}>
				<nav>
					<div className="links-divider">
						<Link href={'/'} className="logo">
							{isDark ? (
								<Image
									src={imageWhite}
									alt="Common ninja logo"
									width={200}
									height={30}
									priority
								/>
							) : (
								<Image
									src={imageBlack}
									alt="Common ninja logo"
									width={200}
									height={30}
									priority
								/>
							)}
						</Link>
						{renderLinks()}
					</div>
					<div className="links-divider">
						<Link
							onClick={() => setSideNavOpen(false)}
							href="https://docs.google.com/forms/u/2/d/e/1FAIpQLScSaQ2nwvM9gkKLmmUnrw_IIqB5s-f3CxLGI2ieX8K2EFaWrw/viewform?usp=send_form"
							target={'_blank'}
						>
							Submit an App
						</Link>
						<Link
							className="login"
							href={'https://www.commoninja.com/login'}
							onClick={() => {
								trackEvent('Website Login');
							}}
						>
							Log In
						</Link>
					</div>
				</nav>
			</StyledHeader>
		</>
	);
};
